import { Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type Group, useGroups } from "@/lib/api";

import { CreateGroupDialog } from "./create-group-dialog";
import { DeleteGroupDialog } from "./delete-group-dialog";
import { GroupMembersDialog } from "./group-members-dialog";
import { GroupTable } from "./group-table";
import { RenameGroupDialog } from "./rename-group-dialog";

export function AdminGroupsPage() {
  const { data: groups = [], isLoading: loading } = useGroups();
  const [search, setSearch] = useState("");

  // Dialog state
  const [membersGroup, setMembersGroup] = useState<Group | null>(null);
  const [renameGroup, setRenameGroup] = useState<Group | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<Group | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return groups;
    }
    const q = search.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-primary text-2xl font-normal">Groups</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Scope project visibility &mdash; {groups.length} group{groups.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 size-4" />
          New Group
        </Button>
      </div>

      {/* Table card */}
      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {/* Search bar inside card */}
        <div className="border-border border-b px-4 py-3">
          <div className="relative max-w-sm">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by group name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">Loading groups...</div>
        ) : (
          <GroupTable
            groups={filtered}
            onManageMembers={setMembersGroup}
            onRename={setRenameGroup}
            onDelete={setDeleteGroup}
          />
        )}
      </div>

      {/* Dialogs */}
      <GroupMembersDialog
        group={membersGroup}
        open={membersGroup !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMembersGroup(null);
          }
        }}
      />

      <RenameGroupDialog
        group={renameGroup}
        open={renameGroup !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameGroup(null);
          }
        }}
      />

      <DeleteGroupDialog
        group={deleteGroup}
        open={deleteGroup !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteGroup(null);
          }
        }}
      />

      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
